import { MultiSelectOption, useToastMessage } from '@equip.health/ui';
import { useEffect, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { SessionGuideEvents } from '~/analytics/sessionGuideEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { AnalyticsEvent } from '~/analytics';
import {
  CarePlanNoteManifestationResponse,
  CarePlanNoteProblemResponse,
} from '~/lib/types/carePlan/carePlans';
import { useUpdateManifestationMutation } from '~/store/services/carePlan';
import { useCarePlanFrequencyOptions } from '~/lib/hooks/useCarePlanFrequencyOptions';
import {
  NOT_YET_ASSESSED_FREQUENCY_CODES,
  RESOLVED_FREQUENCY_CODES,
} from '~/lib/constants/carePlan.constants';
import { PatientNoteFrequencyRow } from '~/components/maud/patients/note/PatientNoteFrequencyRow';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';

type Props = {
  manifestation: CarePlanNoteManifestationResponse;
  noteExternalId: string;
  problem: CarePlanNoteProblemResponse;
  treatmentEpisodeId: string;
  showFrequencyErrors: boolean;
};

export const PatientNoteManifestationFrequencyRow = ({
  manifestation,
  noteExternalId,
  problem,
  treatmentEpisodeId,
  showFrequencyErrors,
}: Props) => {
  const isChanged =
    manifestation.frequencyHistories?.[0]?.noteExternalId === noteExternalId;

  const isNotYetAssessed = NOT_YET_ASSESSED_FREQUENCY_CODES.includes(
    manifestation.frequency?.code || '',
  );

  const { cpFrequencyNoteSigningLogic } = useFlags<LDFlags>();

  const callUpdateManifestation = (frequencyCode: string) => {
    const statusCode = RESOLVED_FREQUENCY_CODES.includes(frequencyCode)
      ? 'RESOLVED'
      : 'ACTIVE';

    updateManifestation({
      manifestationId: manifestation.id,
      treatmentEpisodeId,
      body: {
        frequencyCode,
        noteExternalId,
        statusCode,
      },
    });
  };

  const handleConfirm = () => {
    sendAnalyticsEvent(SessionGuideEvents.sessionGuideFrequencyConfirmed);
    if (manifestation.frequency?.code) {
      callUpdateManifestation(manifestation.frequency.code);
    }
  };

  const { updateFrequencyReadiness, removeFrequencyReadiness } =
    useEditPatientNoteFormContext();
  const { noteId } = useEditNoteDrawerContext();
  const manifestationId = `${problem.id}-${manifestation.pathwayManifestation.code}`;
  const { trackEvent } = useAnalytics();

  const sendAnalyticsEvent = (analyticsEvent: AnalyticsEvent) => {
    trackEvent(analyticsEvent, {
      Source: AnalyticsSourceValues.SESSION_GUIDE,
    });
  };
  const {
    options: frequencyOptions,
    placeholder: frequencyPlaceholder,
    isLoading: isGetFrequenciesLoading,
  } = useCarePlanFrequencyOptions({
    frequencyCode: manifestation.frequency?.code,
    isNoteContext: true,
  });
  const [updateManifestation, { error }] = useUpdateManifestationMutation();
  const { createToast } = useToastMessage();

  const isReadyForSigning = useMemo(() => {
    const goal = problem.goals[0];
    if (goal && goal.status.code === CarePlanGoalStatus.NOT_ASSESSED) {
      return true;
    }

    if (
      cpFrequencyNoteSigningLogic &&
      manifestation.frequency?.required === false
    ) {
      return true;
    }

    const manifestationHistories = manifestation?.frequencyHistories;

    if (!manifestationHistories?.length) {
      return false;
    }

    return manifestationHistories[0].noteExternalId === noteId;
  }, [cpFrequencyNoteSigningLogic, problem.goals, manifestation, noteId]);

  // Register this frequency with the context
  useEffect(() => {
    updateFrequencyReadiness(manifestationId, isReadyForSigning);
    return () => {
      removeFrequencyReadiness(manifestationId);
    };
  }, [
    manifestationId,
    isReadyForSigning,
    updateFrequencyReadiness,
    removeFrequencyReadiness,
  ]);

  useEffect(() => {
    if (error) {
      createToast({
        id: 'patient-note-manifestation-frequency-row-update-error',
        text: 'An error occurred updating the frequency. Please try again later.',
        variant: 'critical',
      });
    }
  }, [error, createToast]);

  const handleFrequencyChange = (newFrequency: MultiSelectOption) => {
    sendAnalyticsEvent(SessionGuideEvents.sessionGuideFrequencyOptionSelected);
    callUpdateManifestation(newFrequency.value);
  };

  const showError = showFrequencyErrors && !isReadyForSigning;

  return (
    <PatientNoteFrequencyRow
      id={manifestation.pathwayManifestation.code}
      isButtonDisabled={isNotYetAssessed}
      isChanged={isChanged}
      isLoading={isGetFrequenciesLoading}
      isRequired={
        cpFrequencyNoteSigningLogic &&
        (manifestation.frequency === null || manifestation.frequency?.required)
      }
      label={
        manifestation.pathwayManifestation.providerFacingLanguage ||
        manifestation.pathwayManifestation.label
      }
      onChange={handleFrequencyChange}
      onConfirm={handleConfirm}
      onMenuOpen={() =>
        sendAnalyticsEvent(
          SessionGuideEvents.sessionGuideFrequencyDropdownClicked,
        )
      }
      options={frequencyOptions}
      placeholder={frequencyPlaceholder}
      showError={showError}
      value={frequencyOptions.find(
        (option) => option.value === manifestation.frequency?.code,
      )}
      frequencyHistories={manifestation.frequencyHistories}
      isReadyForSigning={isReadyForSigning}
    />
  );
};
